import { DEFAULT_EVENT_TYPE_KEY, EVENT_TYPE_OPTIONS, normalizeEventTypeKey } from '../eventTypes';
import { getDishImageUrl } from '../eventImageUtils';
import { formatTimeForApi } from './buildEventFormData';
import {
  COURSE_API_KEYS,
  EVENT_DESCRIPTION_MAX,
  EVENT_SHORT_DESCRIPTION_MAX,
  normalizeFormMultilineField,
  normalizeMultilineText,
} from './eventFormConstants';
import { EVENT_REFUND_POLICY, normalizeRefundPolicy } from './partnerEventSettings';
import {
  ATTENDEE_ELIGIBILITY,
  normalizeAttendeeEligibility,
} from '../../../shared/registrationSettings';
import {
  DEFAULT_EVENT_FORMAT,
  resolveEventFormatFromApi,
  resolveOnlineMeetingLinkFromApi,
} from './eventFormatConstants';
import { normalizeTopicsFromApi } from './eventTopicsConstants';

const BASE_MANAGE_EVENT_STEPS = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'date', label: 'Date & Location' },
  { id: 'menu', label: 'Menu' },
];

const CONFIRMATION_STEP = { id: 'confirmation', label: 'Confirmation' };
const SETTINGS_STEP = { id: 'settings', label: 'Settings & Policies' };

/** Both admin and partner flows include Settings & Policies before Confirmation. */
export const getManageEventSteps = (includeSettingsStep = true) => {
  const steps = [...BASE_MANAGE_EVENT_STEPS];
  if (includeSettingsStep) {
    steps.push(SETTINGS_STEP);
  }
  steps.push(CONFIRMATION_STEP);
  return steps;
};

/** @deprecated Use getManageEventSteps() */
export const MANAGE_EVENT_STEPS = getManageEventSteps();

export { EVENT_TYPE_OPTIONS };

export const DESCRIPTION_MAX = 300;

/** Earliest selectable calendar day (today, local midnight). */
export const getMinSelectableDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/** Event date min — booking deadline constraint disabled for now. */
// export const getEventDateMinDate = (bookingDeadline) => {
//   if (!bookingDeadline) {
//     return getMinSelectableDate();
//   }
//   const min = new Date(bookingDeadline);
//   min.setHours(0, 0, 0, 0);
//   return min;
// };

export const formatTimeRange = (startTime, endTime) => {
  if (!startTime && !endTime) return '';
  if (startTime && endTime) return `${startTime} - ${endTime}`;
  return startTime || endTime;
};

export const parseTimeRange = (timeRange = '') => {
  const parts = String(timeRange)
    .split('-')
    .map((part) => part.trim());
  return {
    startTime: parts[0] ?? '',
    endTime: parts[1] ?? '',
  };
};

export const MENU_COURSES = ['Starter', 'Main Course', 'Dessert'];

let dishCounter = 0;

export const getEmptyDish = () => ({
  id: `dish-${++dishCounter}`,
  dishName: '',
  description: '',
  imagePreview: '',
  imageFile: null,
});

export const getEmptyMenuSection = (course) => ({
  course,
  dishes: [],
});

export const getInitialMenuSections = () =>
  MENU_COURSES.map((course) => getEmptyMenuSection(course));

export const getInitialFormState = () => ({
  title: '',
  type: '',
  price: '',
  description: '',
  wingmanDescription: '',
  topics: [],
  memberMatching: false,
  coverImageFile: null,
  coverImagePreview: '',
  totalSeats: '',
  // bookingDeadline: null,
  eventDate: null,
  startTime: '',
  endTime: '',
  eventFormat: DEFAULT_EVENT_FORMAT,
  venueName: '',
  location: '',
  latitude: '',
  longitude: '',
  onlineMeetingLink: '',
  currency: 'AED',
  menuSections: getInitialMenuSections(),
  menuSkipped: false,
  menuAttachFile: null,
  menuPrefillFile: null,
  enableWaitlist: false,
  inviteOnly: false,
  requireJoinApproval: false,
  attendeeEligibility: ATTENDEE_ELIGIBILITY.ALL,
  refundPolicy: EVENT_REFUND_POLICY.SEVEN_DAY,
});

const parseApiDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const mapMenuItemsToSections = (menuItems = {}) => {
  return MENU_COURSES.map((course) => {
    const apiKey = COURSE_API_KEYS[course];
    const items = menuItems[apiKey] ?? [];

    if (items.length === 0) return getEmptyMenuSection(course);

    return {
      course,
      dishes: items.map((item, index) => ({
        id: String(item.id ?? `${course}-${index}`),
        dishName: item.dishName ?? '',
        description: normalizeMultilineText(item.description ?? ''),
        imagePreview: getDishImageUrl(item),
        imageFile: null,
      })),
    };
  });
};

export const trimField = (value) => String(value ?? '').trim();

const normalizeModerationPrice = (value) => {
  const trimmed = trimField(String(value).replace(/\s*AED$/i, ''));
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? String(parsed) : trimmed;
};

const normalizeModerationSeats = (value) => {
  const trimmed = trimField(value);
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? String(parsed) : trimmed;
};

const appendCopySuffix = (title) => {
  const trimmed = trimField(title);
  if (!trimmed) return '';
  if (/\(copy\)\s*$/i.test(trimmed)) return trimmed;
  return `${trimmed} (Copy)`;
};

/** Pre-fills the add-event form from an existing event (new record). */
export const mapEventToDuplicateFormState = (event) => {
  const base = mapEventToFormState(event);

  return {
    ...base,
    title: appendCopySuffix(base.title),
    eventDate: null,
    coverImageFile: null,
    menuAttachFile: null,
    menuPrefillFile: null,
    menuSections: base.menuSections.map((section) => ({
      ...section,
      dishes: section.dishes.map((dish) => ({
        ...getEmptyDish(),
        dishName: dish.dishName ?? '',
        description: dish.description ?? '',
        imagePreview: dish.imagePreview ?? '',
        imageFile: null,
      })),
    })),
  };
};

export const mapEventToFormState = (event) => {
  if (!event) return getInitialFormState();

  const menuSections = mapMenuItemsToSections(
    event.menuItems ?? { starter: [], main: [], desert: [] },
  );
  const currency = event.currency ?? 'AED';
  const rawPrice = event.price ?? '';
  const priceString =
    typeof rawPrice === 'number'
      ? String(rawPrice)
      : String(rawPrice)
          .replace(new RegExp(`\\s*${currency}$`, 'i'), '')
          .trim();

  return {
    title: event.title ?? '',
    type: normalizeEventTypeKey(event.type) || DEFAULT_EVENT_TYPE_KEY,
    price: priceString,
    description: normalizeFormMultilineField(event.description ?? '', EVENT_DESCRIPTION_MAX),
    wingmanDescription: normalizeFormMultilineField(
      event.wingmanDescription ?? '',
      EVENT_SHORT_DESCRIPTION_MAX,
    ),
    topics: normalizeTopicsFromApi(event.topics),
    memberMatching: Boolean(event.memberMatching),
    coverImageFile: null,
    coverImagePreview: event.coverImageUrl ?? '',
    totalSeats: String(event.totalSeats ?? ''),
    eventDate: parseApiDate(event.eventDate),
    startTime: formatTimeForApi(event.startTime) || event.startTime || '',
    endTime: formatTimeForApi(event.endTime) || event.endTime || '',
    eventFormat: resolveEventFormatFromApi(event),
    venueName: event.venueName ?? '',
    location: event.location ?? '',
    latitude: event.latitude != null ? String(event.latitude) : '',
    longitude: event.longitude != null ? String(event.longitude) : '',
    onlineMeetingLink: resolveOnlineMeetingLinkFromApi(event),
    currency,
    menuSections,
    menuSkipped: false,
    enableWaitlist: Boolean(event.enableWaitlist),
    inviteOnly: Boolean(event.inviteOnly),
    requireJoinApproval: Boolean(event.requireJoinApproval),
    attendeeEligibility: normalizeAttendeeEligibility(event.attendeeEligibility),
    refundPolicy: normalizeRefundPolicy(event.refundPolicy),
  };
};

/** Whether moderation-relevant event fields changed from loaded event. */
export const hasModerationFieldChanges = (form, eventData) => {
  if (!eventData) return false;

  const originalDescription = normalizeFormMultilineField(
    eventData.description ?? '',
    EVENT_DESCRIPTION_MAX,
  );

  return (
    trimField(form.title) !== trimField(eventData.title) ||
    trimField(form.description) !== trimField(originalDescription) ||
    trimField(form.wingmanDescription) !==
      trimField(
        normalizeFormMultilineField(
          eventData.wingmanDescription ?? '',
          EVENT_SHORT_DESCRIPTION_MAX,
        ),
      ) ||
    trimField(form.eventFormat) !== trimField(resolveEventFormatFromApi(eventData)) ||
    trimField(form.onlineMeetingLink) !== trimField(resolveOnlineMeetingLinkFromApi(eventData)) ||
    trimField(form.venueName) !== trimField(eventData.venueName) ||
    trimField(form.location) !== trimField(eventData.location ?? '') ||
    normalizeModerationPrice(form.price) !== normalizeModerationPrice(eventData.price ?? '') ||
    normalizeModerationSeats(form.totalSeats) !==
      normalizeModerationSeats(eventData.totalSeats ?? '')
  );
};

/**
 * When to show EventSubmissionSuccessModal after partner create/edit.
 * - Add event: submit (not draft)
 * - Edit draft: Submit Event
 * - Edit non-draft: Save Changes only if moderation fields changed
 */
export const shouldShowEventSubmissionSuccessModal = ({
  requiresSaveAs,
  isEditMode,
  workflowStatus,
  action,
  form,
  eventData,
}) => {
  if (!requiresSaveAs) return false;

  if (!isEditMode && action === 'submit') return true;

  if (isEditMode && workflowStatus === 'draft' && action === 'submit') return true;

  if (isEditMode && workflowStatus !== 'draft' && action === 'submit') {
    return hasModerationFieldChanges(form, eventData);
  }

  return false;
};
